export function isElementVisible(element: HTMLElement | null): boolean {
  if (!element) return false;
  const style = window.getComputedStyle(element);
  const isVisible =
    style.display !== 'none' &&
    style.visibility !== 'hidden' &&
    style.opacity !== '0';
  return isVisible;
}

function findParentLabel(
  element: HTMLElement | null,
  elementTypeClass: string
): HTMLElement | null {
  if (!element) return null;
  const parentOrgElement = findNearestLiParent(findNearestUlParent(element));
  if (!parentOrgElement) {
    return null;
  }
  const label = parentOrgElement.querySelector(
    elementTypeClass
  ) as HTMLElement | null;
  return label;
}

function findDeepestLastChildLabelOfPreviousSibling(
  element: HTMLElement | null,
  elementTypeClass: string
): HTMLElement | null {
  if (!element) return null;
  const nearestLiParent = findNearestLiParent(element);
  if (!nearestLiParent) {
    return null;
  }
  const previousSibling =
    nearestLiParent.previousElementSibling as HTMLElement | null;
  if (previousSibling) {
    return findLastChildLabel(previousSibling, elementTypeClass);
  }

  return null;
}

function findNextSiblingLabelOfParentLevel(
  element: HTMLElement | null,
  elementTypeClass: string
): Element | null {
  if (!element) return null;
  const parentUl = findNearestUlParent(element);
  const nextLabel = findNextLabelElementOfSameLevel(parentUl, elementTypeClass);
  if (nextLabel) {
    return nextLabel;
  }
  if (parentUl?.parentElement) {
    return findNextSiblingLabelOfParentLevel(
      parentUl.parentElement,
      elementTypeClass
    );
  }
  return null;
}

function findFirstChildLabel(
  element: HTMLElement | null,
  elementTypeClass: string
): HTMLElement | null {
  if (!element) return null;
  const nearestLiParent = findNearestLiParent(element);
  if (!nearestLiParent) {
    return null;
  }
  const childUl = nearestLiParent.querySelector('ul') as HTMLElement | null;
  if (childUl) {
    const firstLi = childUl.firstElementChild as HTMLElement | null;
    if (firstLi) {
      const label = firstLi.querySelector(elementTypeClass) as HTMLElement;
      if (label && isElementVisible(label)) {
        return label;
      }
    }
  }
  return null;
}

function findLastChildLabel(
  element: HTMLElement | null,
  elementTypeClass: string
): HTMLElement | null {
  if (!element) return null;
  const nearestLiParent = findNearestLiParent(element);
  if (!nearestLiParent) {
    return null;
  }
  const childUl = nearestLiParent.querySelector('ul') as HTMLElement | null;
  if (childUl) {
    const lastLi = childUl.lastElementChild as HTMLElement | null;
    if (lastLi) {
      const label = lastLi.querySelector(elementTypeClass) as HTMLElement;
      if (label) {
        const deeperLabel = findLastChildLabel(label, elementTypeClass);
        return deeperLabel && isElementVisible(deeperLabel)
          ? deeperLabel
          : label && isElementVisible(label)
          ? label
          : null;
      }
    }
  }
  return null;
}

function findNearestUlParent(element: HTMLElement | null): HTMLElement | null {
  if (!element) return null;
  if (element?.parentElement?.tagName === 'UL') {
    return element?.parentElement;
  }
  return findNearestUlParent(element.parentElement);
}

function findNearestLiParent(element: HTMLElement | null): HTMLElement | null {
  if (!element) return null;
  if (
    element.tagName === 'LI' ||
    element.classList.contains('root-of-org-tree')
  ) {
    return element;
  }
  return findNearestLiParent(element.parentElement);
}

function findNextLabelElementOfSameLevel(
  element: HTMLElement | null,
  elementTypeClass: string
): Element | null {
  const nearestLiParent = findNearestLiParent(element);
  if (!nearestLiParent) {
    return null;
  }
  let nextSibling = nearestLiParent.nextElementSibling as HTMLElement | null;
  let labelElement = null;
  while (labelElement === null && nextSibling) {
    labelElement = nextSibling.querySelector(elementTypeClass);
    nextSibling = nextSibling.nextElementSibling as HTMLElement | null;
  }

  if (labelElement) {
    return labelElement;
  }
  return null;
}

function findPreviousLabelElementOfSameLevel(
  element: HTMLElement | null,
  elementTypeClass: string
): Element | null {
  const nearestLiParent = findNearestLiParent(element);
  if (!nearestLiParent) {
    return null;
  }
  let previousSibling =
    nearestLiParent.previousElementSibling as HTMLElement | null;
  let labelElement = null;
  while (labelElement === null && previousSibling) {
    labelElement = previousSibling.querySelector(elementTypeClass);
    previousSibling =
      previousSibling.previousElementSibling as HTMLElement | null;
  }

  if (labelElement) {
    return labelElement;
  }
  return null;
}

// Recursive function to find the next focusable tree label in the DOM
export function findNextTreeLabel(
  element: HTMLElement | null,
  elementTypeClass: string
): HTMLElement | null {
  if (!element) return null;

  const firstChildLabel = findFirstChildLabel(element, elementTypeClass);
  if (firstChildLabel) {
    return firstChildLabel as HTMLElement;
  }

  const nextLabelElementOfSameLevel = findNextLabelElementOfSameLevel(
    element,
    elementTypeClass
  );
  if (nextLabelElementOfSameLevel) {
    return nextLabelElementOfSameLevel as HTMLElement;
  }

  const nextSiblingLabelOfParentLevel = findNextSiblingLabelOfParentLevel(
    element,
    elementTypeClass
  );
  if (nextSiblingLabelOfParentLevel) {
    return nextSiblingLabelOfParentLevel as HTMLElement;
  }

  return null;
}

// Recursive function to find the previous focusable tree label in the DOM
export function findPrevTreeLabel(
  element: HTMLElement | null,
  elementTypeClass: string
): HTMLElement | null {
  if (!element) return null;

  const deepestLastChildLabelOfPreviousSibling =
    findDeepestLastChildLabelOfPreviousSibling(element, elementTypeClass);
  if (deepestLastChildLabelOfPreviousSibling) {
    return deepestLastChildLabelOfPreviousSibling as HTMLElement | null;
  }

  const previousLabelElementOfSameLevel = findPreviousLabelElementOfSameLevel(
    element,
    elementTypeClass
  );
  if (previousLabelElementOfSameLevel) {
    return previousLabelElementOfSameLevel as HTMLElement | null;
  }

  const parentLabel = findParentLabel(element, elementTypeClass);
  if (parentLabel) {
    return parentLabel as HTMLElement | null;
  }

  return null;
}
